import type { ReactNode } from 'react'
import { Card } from './Card'
import { cn } from './cn'
import { Skeleton } from './Skeleton'

export interface StatTileProps {
  label: string
  value: ReactNode
  meta?: ReactNode
  /** Optional embedded visual (Sparkbars / Meter). */
  visual?: ReactNode
  /** Render the honest empty posture when no value has been ingested yet. */
  empty?: boolean
  emptyLabel?: string
  loading?: boolean
  className?: string
}

/**
 * KPI tile built on Card. Renders an honest "—" + empty label when `empty`,
 * never a fabricated zero that implies ingested data.
 */
export function StatTile({
  label,
  value,
  meta,
  visual,
  empty = false,
  emptyLabel = 'Will populate once ingestion is live',
  loading = false,
  className = ''
}: StatTileProps) {
  return (
    <Card className={cn('h-full', className)}>
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
        {loading ? (
          <span role="status" aria-live="polite" aria-label="Loading">
            <Skeleton width="w-24" height="h-8" />
          </span>
        ) : empty ? (
          <>
            <span className="text-h3 font-display text-ink-muted">—</span>
            <span className="text-xs leading-5 text-ink-muted">{emptyLabel}</span>
          </>
        ) : (
          <>
            <span className="text-h3 font-display text-strong tabular-nums">{value}</span>
            {meta ? <span className="text-xs leading-5 text-ink-muted">{meta}</span> : null}
          </>
        )}
        {!empty && !loading && visual ? <div className="mt-2">{visual}</div> : null}
      </div>
    </Card>
  )
}
